import { useEffect, useMemo } from 'react'

import { useDeviceCSVProcessor } from '../hooks/useDeviceCSVProcessor'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'

import {
  Button as DarkButton,
  DownloadIcon,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'

import {
  Button,
  ControlledSelect,
  FormFileUploadControl,
  FormInputSelectControl,
  StyledFormInputControl,
  Table,
  TableBody,
  TableHeader,
} from '@/components/ui'
import { DeviceType } from '@/graphql/generated/schemas'

import { FormInputsIProps } from '../types/types'
import { CSVParseResultI } from '../utils/csvValidator'
import { createColumns } from '../utils/tableColumns'
import {
  deviceTypes,
  getPerTypeCSVTemplate,
  isValidFileType,
} from '../utils/utils'

const CSV_TEMPLATE = [
  'label,name,device_type,facility_name,floor_name,external_id,alarm_type',
  ',Motion Sensor 1,ALARM,Main Office,Floor 1,,Intrusion Alert',
  'Front Lobby,Camera 1,CAMERA,Main Office,Floor 1,ext-cam-001,',
  'Main Entrance,Door 1,DOOR,Main Office,Floor 1,ext-door-001,',
].join('\n')

interface BulkUploadFormIProps {
  form: UseFormMethods<FormInputsIProps>
  parseResult?: CSVParseResultI | null
  onParseResultChange?: (result: CSVParseResultI | null) => void
  onIsParsingChange?: (isParsing: boolean) => void
  isRemoveDisabled?: boolean
  /**
   * Rebrand: keys the hint copy and the file field to the theme. Omit to keep
   * the legacy appearance untouched.
   */
  colorScheme?: 'dark' | 'light'
}
export const BulkUploadForm = ({
  form,
  parseResult,
  onParseResultChange,
  onIsParsingChange,
  isRemoveDisabled = false,
  colorScheme,
}: BulkUploadFormIProps) => {
  const { t } = useTranslation()
  const mutedColor = colorScheme ? `text.secondary.${colorScheme}` : 'gray.600'
  const attachButtonTextColor = colorScheme
    ? tokenVar(`colors.${tokenPrefix(colorScheme)}text.secondary`)
    : undefined
  const isUpdatedBulkUpload = useFeatureFlag(
    FeatureFlag.updatedDeviceBulkUpload
  )
  const isEventPromotionAlarms = useFeatureFlag(
    FeatureFlag.useEventPromotionAlarms
  )
  const { register, control, errors, watch, trigger } = form

  const triggerCSVDownload = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleDownloadTemplate = () => {
    const content = deviceType?.value
      ? getPerTypeCSVTemplate(deviceType.value, isEventPromotionAlarms)
      : CSV_TEMPLATE
    triggerCSVDownload(content, 'device_upload_template.csv')
  }
  const fileUpload = watch('file')
  const deviceType = watch('type')
  const { isLoading } = useDeviceCSVProcessor(
    isUpdatedBulkUpload ? fileUpload : null,
    onParseResultChange ?? (() => {})
  )

  useEffect(() => {
    onIsParsingChange?.(!!isLoading)
  }, [isLoading, onIsParsingChange])

  const columns = useMemo(() => createColumns(), [])
  const table = useReactTable({
    data: parseResult?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })
  const templateDownloads = [
    {
      deviceType: DeviceType.Alarm,
      filename: 'alarm_template.csv',
      label: t('devices:crud_alarm_template'),
    },
    {
      deviceType: DeviceType.Door,
      filename: 'door_template.csv',
      label: t('devices:crud_door_template'),
    },
    {
      deviceType: DeviceType.Camera,
      filename: 'camera_template.csv',
      label: t('devices:crud_camera_template'),
    },
  ]

  if (!isUpdatedBulkUpload) {
    return (
      <Box>
        <Box mb='3'>
          {colorScheme ? (
            <ControlledSelect
              colorScheme={colorScheme}
              control={control}
              hasWarning={!!errors?.type}
              isClearable
              label={t('devices:crud_type_label')}
              name='type'
              options={deviceTypes}
              placeholder={t('devices:crud_type_selection')}
              rules={{ required: true }}
              warningText={t('devices:crud_type_required')}
            />
          ) : (
            <FormInputSelectControl
              control={control}
              errorMessage={!!errors?.type && 'Select a device type'}
              id='type'
              isClearable
              isInvalid={!!errors?.type}
              label='Device Type'
              options={deviceTypes}
              placeholder='Device Type Selection'
              rules={{ required: true }}
            />
          )}
        </Box>
        <Box display='flex' flexDirection='column' gap={2} mb={2}>
          {isEventPromotionAlarms && deviceType?.value === DeviceType.Alarm && (
            <Text color={mutedColor} fontSize='sm'>
              For alarm devices, include an <code>alarm_type</code> column with
              the incident type name or slug.
            </Text>
          )}
          {colorScheme ? (
            <DarkButton
              colorScheme={colorScheme}
              leftIcon={<DownloadIcon colorScheme={colorScheme} size={16} />}
              onClick={handleDownloadTemplate}
              style={{ marginRight: 'auto' }}
              variant='secondary'
            >
              Download CSV Template
            </DarkButton>
          ) : (
            <Button
              onClick={handleDownloadTemplate}
              style={{ marginRight: 'auto' }}
              type='button'
              variant='secondary'
            >
              Download CSV Template
            </Button>
          )}
        </Box>
        <StyledFormInputControl mb={3}>
          <Tooltip label='Supported file types include CSV' shouldWrapChildren>
            <FormFileUploadControl
              accept='.csv'
              attachButtonTextColor={attachButtonTextColor}
              colorScheme={colorScheme}
              dataTestId='createFloor_modal_floorplan'
              errorMessage={
                fileUpload &&
                !isValidFileType(fileUpload) &&
                'File should be in .csv format'
              }
              id='file'
              inputRef={register({ required: true })}
              isInvalid={!isValidFileType(fileUpload)}
              label='Devices'
              onFileRemoved={() => trigger(['file'])}
              placeholder='Attach CSV'
            />
          </Tooltip>
        </StyledFormInputControl>
      </Box>
    )
  }

  return (
    <Box px='6'>
      <Box display='flex' flexDirection='column' gap={2} mb={3}>
        {isEventPromotionAlarms && (
          <Text color={mutedColor} fontSize='sm'>
            For alarm devices, include an <code>alarm_type</code> column with
            the incident type name or slug.
          </Text>
        )}
      </Box>
      <StyledFormInputControl mb={3}>
        <Tooltip
          isDisabled={isValidFileType(fileUpload)}
          label='Supported file types include CSV'
          shouldWrapChildren
        >
          <FormFileUploadControl
            accept='.csv'
            attachButtonTextColor={attachButtonTextColor}
            colorScheme={colorScheme}
            dataTestId='createDevice_modal_bulkUpload'
            errorMessage={
              fileUpload &&
              !isValidFileType(fileUpload) &&
              'File should be in .csv format'
            }
            id='file'
            inputRef={register({ required: true })}
            isDisabled={isRemoveDisabled}
            isInvalid={!isValidFileType(fileUpload)}
            label='Devices'
            onFileRemoved={() => {
              trigger(['file'])
              onParseResultChange?.(null)
            }}
            placeholder='Attach CSV'
          />
        </Tooltip>
      </StyledFormInputControl>
      <Box display='flex' flexWrap='wrap' gap={3} mb={3}>
        {templateDownloads.map(({ deviceType, filename, label }) => {
          const onDownload = () =>
            triggerCSVDownload(
              getPerTypeCSVTemplate(deviceType, isEventPromotionAlarms),
              filename
            )

          return colorScheme ? (
            <DarkButton
              colorScheme={colorScheme}
              key={deviceType}
              leftIcon={<DownloadIcon colorScheme={colorScheme} size={16} />}
              onClick={onDownload}
              size='sm'
              variant='secondary'
            >
              {label}
            </DarkButton>
          ) : (
            <Button
              key={deviceType}
              onClick={onDownload}
              type='button'
              variant='link'
            >
              {label}
            </Button>
          )
        })}
      </Box>

      {isLoading && (
        <Box mb={3}>
          <Text color={mutedColor} fontSize='sm'>
            Parsing CSV file...
          </Text>
        </Box>
      )}

      {parseResult && parseResult.errors.length > 0 && (
        <Alert mb={3} status='error'>
          <AlertIcon />
          <VStack align='start' spacing={1}>
            <AlertTitle>CSV Parsing Errors</AlertTitle>
            <AlertDescription>
              {parseResult.errors.map((error, index) => (
                <Text fontSize='sm' key={index}>
                  • {error}
                </Text>
              ))}
            </AlertDescription>
          </VStack>
        </Alert>
      )}

      {parseResult && parseResult.data.length > 0 && (
        <Box mb={3}>
          <Table>
            <TableHeader table={table} />
            <TableBody rowModel={table.getRowModel()} />
          </Table>
        </Box>
      )}
    </Box>
  )
}
